import React from 'react';
import { makeStyles } from '@mui/styles';
import { Product } from '../../../../../../models/interfaces/Product.interface';
import { ProductCategory } from '../../../../../../models/interfaces/ProductCategory.interface';
import businessObjectService from '../../../../../../services/business-object.service';
import { useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import { Button, Grid, MenuItem, TextField, Toolbar } from '@mui/material';
import { Cancel, Delete, Edit, Save } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import routeService from '../../../../../../routes/route.service';
import { customTheme } from '../../../../../../styles/theme';

const useStyles = makeStyles({
  paper: {
    marginBottom: '20px',
    padding: '20px',
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'flex-end',
    flexDirection: 'column',
  },
  toolbar: {
    width: '100%',
    padding: 0,
  },
  title: {
    flexGrow: 1,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: customTheme.spacing(1),
  },
  deleteButton: {
    marginRight: customTheme.spacing(1),
  },
  editButton: {
    width: 'fit-content',
  },
  submit: {
    margin: customTheme.spacing(3, 0, 2),
  },
});

const productDurationList = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 75, 90, 105, 120, 180];

interface ProductFormComponentProps {
  ownerId: number;
  businessObjectId: number;
  categoryId: number;
  ownerEmail?: string;
  product?: Product;
}

export default function ProductFormComponent(props: ProductFormComponentProps) {
  const navigate = useNavigate();

  const [isEmitMode, setIsEmitMode] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const [product, setProduct] = React.useState<Product>(null);
  const [productToSubmit, setProductToSubmit] = React.useState<Product>(null);

  React.useEffect(() => {
    setIsEmitMode(!props?.product);

    const initCategory: ProductCategory = props?.product
      ? { ...props.product }
      : businessObjectService.createBasicProduct(props.ownerEmail, props.businessObjectId, props.categoryId);

    setProduct(initCategory);
    setProductToSubmit(initCategory);
  }, [props]);

  const submitDataChangeHandler = (event: any) => {
    const { name, value, type, checked } = event.target;

    // @ts-ignore
    setProductToSubmit({
      ...productToSubmit,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const reset = () => {
    setProductToSubmit(
      product || businessObjectService.createBasicProduct(props.ownerEmail, props.businessObjectId, props.categoryId)
    );
    setIsEmitMode(false);
  };

  const submit = async (event: any) => {
    event.preventDefault();

    setIsSubmitting(true);

    let response: Product;
    if (productToSubmit?.id) {
      response = await businessObjectService.updateProduct(productToSubmit);
    } else {
      response = await businessObjectService.createProductForCategory(productToSubmit);

      if (response?.id) {
        navigate(
          routeService.getAndFillProductDetailPath(props.ownerId, props.businessObjectId, props.categoryId, response.id)
        );
      }
    }

    setProduct(response);
    setProductToSubmit(response);

    setIsEmitMode(false);
    setIsSubmitting(false);
  };

  const deleteProduct = async (event: any) => {
    event.preventDefault();

    if (!product?.id) {
      return;
    }

    const response = await businessObjectService.deleteProduct(product);

    if (response) {
      navigate(routeService.getAndFillCategoryDetailPath(props.ownerId, props.businessObjectId, props.categoryId));
    }
  };

  const classes = useStyles();

  return (
    <Paper className={classes.paper}>
      <Toolbar className={classes.toolbar}>
        {productToSubmit?.id && isEmitMode ? (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Delete />}
            disabled={isSubmitting}
            onClick={deleteProduct}
            className={classes.deleteButton}
          >
            Delete
          </Button>
        ) : (
          <></>
        )}

        <Typography variant="h6" className={classes.title}>
          Product Info
        </Typography>

        {/*Edit button*/}
        {productToSubmit?.id && !isEmitMode ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Edit />}
            disabled={isSubmitting}
            onClick={() => {
              setIsEmitMode(true);
            }}
            className={classes.editButton}
          >
            Edit
          </Button>
        ) : (
          <></>
        )}
      </Toolbar>

      <form className={classes.form} onSubmit={submit}>
        {/*ID*/}
        {productToSubmit?.id ? (
          <TextField
            variant="outlined"
            margin="dense"
            type="number"
            fullWidth
            key="id"
            label="ID"
            name="id"
            value={productToSubmit?.id}
            disabled={true}
          />
        ) : (
          <></>
        )}

        {/*Name*/}
        <TextField
          variant="outlined"
          margin="dense"
          type="text"
          required
          fullWidth
          key="name"
          label="Product name"
          name="name"
          value={productToSubmit?.name || ''}
          disabled={!isEmitMode}
          onChange={submitDataChangeHandler}
        />

        {/*Description*/}
        <TextField
          variant="outlined"
          margin="dense"
          type="text"
          fullWidth
          key="description"
          label="Description"
          name="description"
          value={productToSubmit?.description || ''}
          disabled={!isEmitMode}
          onChange={submitDataChangeHandler}
        />

        {/*Price*/}
        <TextField
          variant="outlined"
          margin="dense"
          type="number"
          required
          fullWidth
          label="Price"
          name="price"
          value={productToSubmit?.price || ''}
          disabled={!isEmitMode}
          onChange={submitDataChangeHandler}
        />

        {/*Duration*/}
        <TextField
          variant="outlined"
          margin="dense"
          select
          required
          fullWidth
          label="Duration"
          name="duration"
          value={productToSubmit?.duration || ''}
          disabled={!isEmitMode}
          onChange={submitDataChangeHandler}
          placeholder="Please select duration"
        >
          {productDurationList.map(duration => (
            <MenuItem value={duration} key={duration}>
              {duration} minutes
            </MenuItem>
          ))}
        </TextField>

        {/*Submit button*/}
        {isEmitMode ? (
          <Grid container spacing={3}>
            <Grid item xs={6} hidden={!productToSubmit?.id}>
              <Button
                fullWidth
                variant="outlined"
                className={classes.submit}
                disabled={isSubmitting}
                onClick={reset}
                startIcon={<Cancel />}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={productToSubmit?.id ? 6 : 12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={isSubmitting}
                startIcon={<Save />}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        ) : (
          <></>
        )}
      </form>
    </Paper>
  );
}
